import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * The words Numbers & SIP uses for a customer SIP trunk (plan-008 ST-UN, design §22.9.4, §22.10.2,
 * §22.14). Pure, so each sentence is pinned by a test rather than by a screenshot.
 *
 * **Every sentence names what was observed, never what was hoped.** A check that got no answer is
 * "no SIP server answered", not "failed"; a leg whose codec was not reported is "Codec not
 * reported", not a default; a certificate that could not be verified is never called secure. WeTalk's
 * SIP host and address come from `readSipTrunkSettings` and are passed in — no host is written here.
 */
import type {
  CallerIdPresentation,
  SipCheckOutcome,
  SipCheckOutcomeKind,
  SipCheckTls,
  SipCodecPolicy,
  SipInboundDelivery,
  SipMediaEncryption,
  SipRegistrationState,
  SipTransport,
  SipTrunkFieldErrorCode,
  SipTrunkInboundNotReadyReason,
  SipTrunkState,
  WireNumberFormat,
} from "@wetalk/domain";

import type { SipTrunkEventKind, SipTrunkEventView, SipTrunkRegistrationView } from "../../api/slice/sip-trunk.js";

/** Design §22.10.2, verbatim: the one HD sentence every surface uses. */
export const HD_SENTENCE =
  () => (portalText("operations", "hd_voice_depends_on_your_provider_and_on_the_other_end_con_4f734bc4"));

/** Who bills what — the docs article and the dialog say the same (R-UI-10). */
export const TRUNK_BILLING_SENTENCE =
  () => (portalText("operations", "your_provider_bills_its_own_minutes_for_calls_through_your_892eae3a"));

/**
 * `409 sip_trunk_limit_reached` (plan-008 ST-RV2, review C1): the account holds its most trunks. The
 * number is stated only when the API sent it; otherwise the sentence names none rather than a guess.
 */
export function trunkMaximumSentence(maximum: number | null): string {
  const limit = maximum === null ? portalText("operations", "the_limit_of_phone_systems_aed38f1d") : portalText("operations", "the_limit_of_value_phone_systems_42fe9a1d", { v0: String(String(maximum)) });
  return portalText("operations", "you_ve_reached_value_for_this_account_remove_one_you_no_lo_9426273f", { v0: String(limit) });
}

export const TRANSPORT_WORD: Readonly<Record<SipTransport, string>> = { udp: "UDP", tcp: "TCP", tls: "TLS" };

/** The three encryption choices in the dialog's words (§22.14). */
export const MEDIA_ENCRYPTION_WORD: Readonly<Record<SipMediaEncryption, string>> = {
  get none() { return portalText("operations", "none_6eef6648"); },
  get srtp_optional() { return portalText("operations", "if_the_provider_offers_it_f40c421c"); },
  get srtp_required() { return portalText("operations", "always_needs_tls_1f2e28e6"); },
};

export const CODEC_POLICY_WORD: Readonly<Record<SipCodecPolicy, string>> = {
  get wideband_first() { return portalText("operations", "hd_first_b6c40edf"); },
  get alaw_only() { return portalText("operations", "hd_else_a_law_only_ddd017d5"); },
};

export const WIRE_NUMBER_FORMAT_WORD: Readonly<Record<WireNumberFormat, string>> = {
  get e164_plus() { return portalText("operations", "international_with_574688a4"); },
  get e164() { return portalText("operations", "international_without_d9b315d3"); },
  get international_00() { return portalText("operations", "international_with_00_fd259c86"); },
  get national() { return portalText("operations", "national_710c85ef"); },
};

export const CALLER_ID_PRESENTATION_WORD: Readonly<Record<CallerIdPresentation, string>> = {
  get number_e164_plus() { return portalText("operations", "the_number_international_with_c4e15d64"); },
  get number_national() { return portalText("operations", "the_number_in_national_form_d47b1036"); },
  get account_name() { return portalText("operations", "your_sip_username_some_providers_present_the_number_from_i_2b3c49c3"); },
};

export const TRUNK_STATE_WORD: Readonly<Record<SipTrunkState, string>> = {
  get unverified() { return portalText("operations", "not_verified_yet_ae8be834"); },
  get verified() { return portalText("operations", "verified_aed3b8c6"); },
  get disabled() { return portalText("operations", "disabled_f4f4473d"); },
};

/**
 * plan-015 SIP (ADR 0046): an INBOUND-ONLY trunk's state — it is not verified by a check (there is nothing to
 * check) but by its first answered call, so "Not verified yet" says what it is waiting for.
 */
export const INBOUND_ONLY_TRUNK_STATE_WORD: Readonly<Record<SipTrunkState, string>> = {
  get unverified() { return portalText("operations", "waiting_for_the_first_call_053d7b03"); },
  get verified() { return portalText("operations", "verified_aed3b8c6"); },
  get disabled() { return portalText("operations", "disabled_f4f4473d"); },
};

/** The card's outbound row on an inbound-only trunk: what it lacks, and how to add it (Owners and Admins). */
export function inboundOnlyOutboundSentence(canManage: boolean): string {
  return (
    portalText("operations", "none_this_trunk_only_receives_calls_so_wetalk_does_not_cal_89f1c5a8", { v0: String(canManage ? portalText("operations", "to_call_out_too_add_your_provider_s_server_address_port_us_11fdb048") : "") })
  );
}

/** The card's status on an inbound-only trunk, in place of the connection check it never has. */
export const INBOUND_ONLY_STATUS_SENTENCE =
  () => (portalText("operations", "nothing_to_check_calls_arrive_from_your_provider_the_first_e82d2f93"));

/** Said at the top of the edit dialog of an inbound-only trunk. */
export const INBOUND_ONLY_EDIT_SENTENCE =
  () => (portalText("operations", "this_trunk_only_receives_calls_to_call_out_through_it_too_3dd72b23"));

/** A number marked "Use as caller ID" on a trunk that has no outbound details. */
export const INBOUND_ONLY_CALLER_ID_WORD =
  () => (portalText("operations", "a_trunk_that_only_receives_calls_presents_no_caller_id_add_11aea8d9"));

/** Why inbound is not ready, in the first-failing order of design §22.5.2 (B7). */
export const INBOUND_NOT_READY_WORD: Readonly<Record<SipTrunkInboundNotReadyReason, string>> = {
  get inbound_off() { return portalText("operations", "receiving_conversations_through_your_own_trunk_is_not_swit_fc9a33a9"); },
  get disabled() { return portalText("operations", "the_trunk_is_disabled_8e54f43d"); },
  get not_verified() { return portalText("operations", "verify_the_trunk_first_check_the_connection_or_place_a_tes_b065fceb"); },
  get no_allowed_address() { return portalText("operations", "add_your_provider_s_signalling_addresses_edit_allowed_addr_5050e631"); },
  get no_inbound_auth() { return portalText("operations", "create_wetalk_s_sign_in_details_for_your_provider_below_72c9b4d6"); },
  get no_bound_number() { return portalText("operations", "connect_one_of_the_trunk_s_numbers_to_an_agent_aa40170f"); },
  get not_registered() { return portalText("operations", "waiting_for_your_provider_to_accept_wetalk_s_sign_in_c458f472"); },
};

// ── How the provider sends calls (plan-008 ST-KM) ──────────────────────────────────────────

/** The dialog's question, and its two answers in plain words. */
export const INBOUND_DELIVERY_QUESTION = () => (portalText("operations", "how_does_your_provider_send_you_calls_8c171a70"));

export const INBOUND_DELIVERY_CHOICE_WORD: Readonly<Record<SipInboundDelivery, string>> = {
  get registration() { return portalText("operations", "it_signs_in_to_my_phone_system_registration_5e960edf"); },
  get direct() { return portalText("operations", "it_sends_calls_to_an_address_i_give_it_direct_9d7b38f0"); },
};

/** The card's short line for the delivery. */
export const INBOUND_DELIVERY_WORD: Readonly<Record<SipInboundDelivery, string>> = {
  get registration() { return portalText("operations", "your_provider_sends_calls_to_wetalk_s_registration_510e1394"); },
  get direct() { return portalText("operations", "your_provider_sends_calls_to_wetalk_s_address_aa3c192d"); },
};

/** Said beside the registration choice: what signing in on the member's behalf means. */
export const REGISTRATION_WARNING =
  () => (portalText("operations", "wetalk_signs_in_to_your_provider_as_your_phone_system_if_a_dfb9a413"));

/** Registration runs over UDP only for now; said when another transport is chosen. */
export const REGISTRATION_NEEDS_UDP = () => (portalText("operations", "registration_works_over_udp_only_choose_udp_as_the_transpo_59488ca0"));

/** What a registration trunk's card says instead of offering inbound sign-in details. */
export const REGISTRATION_INBOUND_SENTENCE =
  () => (portalText("operations", "your_provider_delivers_calls_to_wetalk_s_registration_so_i_417e2c09"));

/** The registration front's report in words — only what it reported (R-OBS-2). */
export function registrationStateWords(registration: SipTrunkRegistrationView | null): string {
  const state: SipRegistrationState = registration === null ? "unregistered" : registration.state;
  switch (state) {
    case "registered":
      return portalText("operations", "registered_with_your_provider_a9f4d7f2");
    case "trying":
      return portalText("operations", "registering_with_your_provider_d3134548");
    case "failed":
      return registration?.reasonCode == null ? portalText("operations", "registration_failed_8bfb1a97") : portalText("operations", "registration_failed_sip_value_e120846d", { v0: String(String(registration.reasonCode)) });
    case "unregistered":
      return portalText("operations", "not_registered_with_your_provider_ad508673");
  }
}

/** `Registered with your provider since 2 Oct, 10:03` — the state with when it was first reported. */
export function registrationLineOf(registration: SipTrunkRegistrationView | null): string {
  const words = registrationStateWords(registration);
  return registration === null ? words : portalText("operations", "value_since_value_931ec2ae", { v0: String(words), v1: String(registration.observedAtText) });
}

export function registrationTone(registration: SipTrunkRegistrationView | null): CheckTone {
  if (registration?.state === "registered") return "positive";
  if (registration?.state === "failed") return "critical";
  return "caution";
}

/** What WeTalk's own SIP edge is, for the sentences that tell a member to allow-list it. */
export interface SipEdgeAddressView {
  readonly sipEdgeHost: string;
  readonly sipEdgeAddress: string;
}

/** The trunk facts the check's words name. */
export interface CheckedTrunkView {
  readonly host: string;
  readonly port: number;
  readonly transport: SipTransport;
}

export type CheckTone = "positive" | "caution" | "critical";

export interface CheckWords {
  /** The design's sentence for the outcome (§22.9.4). */
  readonly sentence: string;
  /** The TLS qualifier, when the outcome reached the transport over TLS; `null` otherwise. */
  readonly tls: string | null;
  readonly tone: CheckTone;
}

function allowListHint(edge: SipEdgeAddressView | null): string {
  return edge === null
    ? portalText("operations", "wetalk_s_sip_address_under_where_to_point_your_provider_ee34f849")
    : `${edge.sipEdgeHost} (${edge.sipEdgeAddress})`;
}

/** The qualifier on an outcome that reached the transport. `tls_unverified` is never "secure". */
export function tlsWords(tls: SipCheckTls): string | null {
  if (tls === "tls_unverified") {
    return portalText("operations", "connected_securely_but_the_certificate_could_not_be_verifi_0f2de3fa");
  }
  if (tls === "tls_ok") return portalText("operations", "connected_securely_tls_the_certificate_was_verified_eafac0c3");
  return null;
}

function toneOf(kind: SipCheckOutcomeKind): CheckTone {
  if (kind === "credentials_accepted" || kind === "sip_reachable_unauthenticated") return "positive";
  if (kind === "not_verified") return "caution";
  return "critical";
}

/** A fresh check's outcome in the design's words — with the SIP code, phrase and TLS qualifier. */
export function checkOutcomeWords(
  outcome: SipCheckOutcome,
  trunk: CheckedTrunkView,
  edge: SipEdgeAddressView | null,
): CheckWords {
  const tls = "tls" in outcome ? tlsWords(outcome.tls) : null;
  const tone = toneOf(outcome.kind);
  switch (outcome.kind) {
    case "credentials_rejected":
      return {
        get sentence() { return portalText("operations", "your_trunk_is_reachable_but_the_username_or_password_was_r_6262b723", { v0: String(String(outcome.statusCode)) }); },
        tls,
        tone,
      };
    case "sip_error":
      return {
        get sentence() { return portalText("operations", "your_provider_answered_sip_value_value_59ad6692", { v0: String(String(outcome.statusCode)), v1: String(outcome.reasonPhrase) }); },
        tls,
        tone,
      };
    default:
      return { sentence: checkKindSentence(outcome.kind, trunk, edge), tls, tone };
  }
}

/**
 * The last check as the card reads it: only the kind is stored on the trunk, so the code and phrase
 * of a refusal are not repeated — the words say what is known and no more.
 */
export function checkKindSentence(
  kind: SipCheckOutcomeKind,
  trunk: CheckedTrunkView,
  edge: SipEdgeAddressView | null,
): string {
  switch (kind) {
    case "dns_failed":
      return portalText("operations", "we_could_not_find_value_check_the_host_name_99169b48", { v0: String(trunk.host) });
    case "blocked_destination":
      return portalText("operations", "that_address_is_not_a_public_host_we_can_reach_6b7902ac");
    case "unreachable":
      return portalText("operations", "we_could_not_connect_to_value_value_over_value_95265b95", { v0: String(trunk.host), v1: String(String(trunk.port)), v2: String(TRANSPORT_WORD[trunk.transport]) });
    case "tls_failed":
      return portalText("operations", "the_secure_tls_connection_failed_e55f0391");
    case "not_verified":
      return (
        portalText("operations", "we_reached_value_but_no_sip_server_answered_if_your_provid_ecc12d18", { v0: String(trunk.host), v1: String(allowListHint(edge)) })
      );
    case "sip_reachable_unauthenticated":
      return (
        portalText("operations", "your_trunk_is_reachable_your_provider_did_not_ask_us_to_si_6c6c04dd")
      );
    case "credentials_accepted":
      return portalText("operations", "your_trunk_is_reachable_and_your_username_and_password_wer_6c44e0a1");
    case "credentials_rejected":
      return portalText("operations", "your_trunk_is_reachable_but_the_username_or_password_was_r_3ff1dac7");
    case "sip_error":
      return portalText("operations", "your_provider_answered_with_a_sip_error_3f55ba3f");
  }
}

export function checkKindTone(kind: SipCheckOutcomeKind): CheckTone {
  return toneOf(kind);
}

/**
 * A negotiated codec in design §22.10.2's words. Only G.722 and AMR-WB are HD; G.711 is standard,
 * and an unobserved codec says so rather than defaulting to either.
 */
export function codecWords(codec: string | null): string {
  switch (codec) {
    case null:
      return portalText("operations", "codec_not_reported_54f943ed");
    case "G722":
      return portalText("operations", "hd_g_722_16_khz_dcc6da57");
    case "AMR-WB":
      return portalText("operations", "hd_amr_wb_16_khz_b6e9a5ea");
    case "PCMA":
      return portalText("operations", "standard_g_711_a_law_8_khz_629fc7cf");
    case "PCMU":
      return portalText("operations", "standard_g_711_law_8_khz_47264af2");
    default:
      return codec;
  }
}

/** The codecs a trunk offers, in order: `G.722 (HD), G.711 µ-law, G.711 A-law`. */
export function offeredCodecWords(codec: readonly string[]): string {
  const word: Readonly<Record<string, string>> = { G722: portalText("operations", "g_722_hd_686fbd93"), PCMU: portalText("operations", "g_711_law_5ba5e7f4"), PCMA: portalText("operations", "g_711_a_law_1628aa51") };
  return codec.map((one) => word[one] ?? one).join(", ");
}

/** How a leg ended (`end_reason`), in plain words; an unknown reason is shown as recorded. */
export function endReasonWords(endReason: string): string {
  const word: Readonly<Record<string, string>> = {
    in_progress: portalText("operations", "in_progress_b6bd42e4"),
    caller_hangup: portalText("operations", "the_caller_hung_up_bf39096d"),
    assistant_hangup: portalText("operations", "the_agent_ended_it_ad236a14"),
    // plan-022 (design §34): the agent ended a finished conversation itself.
    voice_agent_ended: portalText("operations", "the_agent_ended_the_conversation_a9ef70e9"),
    transferred: portalText("operations", "transferred_f76d1e2a"),
    no_answer: portalText("operations", "no_answer_a9c16dd0"),
    busy: portalText("operations", "busy_592a9d16"),
    unreachable: portalText("operations", "could_not_be_reached_f1e8f843"),
    carrier_failure: portalText("operations", "could_not_be_connected_7ce95a18"),
    runtime_error: portalText("operations", "ended_by_an_error_at_wetalk_055bc3ce"),
    runtime_lost: portalText("operations", "ended_by_an_error_at_wetalk_055bc3ce"),
    // design §46.1 (2026-11-01): wetalk gave up before the dial — nobody was rung.
    not_dialled: portalText("operations", "end_reason.not_dialled"),
    overflow_message_taken: portalText("finance", "merge.numbers.overflowMessage"),
    // 2026-10-03 (fault containment): one conversation ended by a fault the runtime contained.
    runtime_fault: portalText("operations", "ended_by_a_technical_problem_dbefff7a"),
    voice_agent_paused: portalText("operations", "the_agent_is_paused_f7ff396f"),
    credit_exhausted: portalText("operations", "out_of_credit_e11178b0"),
    trial_exhausted: portalText("operations", "trial_used_up_741a2a07"),
    hard_stop_reached: portalText("operations", "stopped_at_the_spending_limit_21735578"),
    budget_reached: portalText("operations", "stopped_at_the_workspace_budget_b2d9618b"),
    // plan-014 E4 gate 4 (2026-10-07): the plan's allowance is spent and it sells no overage.
    entitlement_exhausted: portalText("operations", "plan_allowance_used_up_9af64399"),
    platform_at_capacity: portalText("operations", "all_lines_were_busy_70c7d24d"),
    max_duration_reached: portalText("operations", "reached_the_maximum_length_322d61cb"),
    watchdog_silence: portalText("operations", "ended_after_a_long_silence_f6498a04"),
    // plan-031 VMD-WAPP: a voicemail answered a test or ring-now leg and the runtime hung up at once (OD-7).
    voicemail: portalText("shell", "voicemail.outcome"),
  };
  return word[endReason] ?? endReason.replaceAll("_", " ");
}

const EVENT_KIND_WORD: Readonly<Record<SipTrunkEventKind, string>> = {
  get invite_in() { return portalText("operations", "incoming_call_7af30b79"); },
  get invite_out() { return portalText("operations", "outgoing_call_27a23716"); },
  get final_response() { return portalText("operations", "final_response_0b5d4da9"); },
  get answered() { return portalText("operations", "answered_e0aafffa"); },
  get bye() { return portalText("operations", "hung_up_add9ea21"); },
  get check() { return portalText("operations", "connection_check_b3df45dd"); },
  get auth_failure() { return portalText("operations", "sign_in_refused_75eab799"); },
  get unmatched_invite() { return portalText("operations", "call_to_a_number_not_on_this_trunk_cb14aaee"); },
  get refused() { return portalText("operations", "refused_735ec50d"); },
  get state_changed() { return portalText("operations", "status_changed_703b211b"); },
};

/**
 * plan-015 SIP (ADR 0046): the API's `verified_by_first_call` — the slice reads it as the `state_changed` it is,
 * marked `verifiedByFirstCall` — has its own words: an inbound-only trunk's first answered call verified it.
 */
const VERIFIED_BY_FIRST_CALL_WORD = () => (portalText("operations", "verified_by_its_first_call_63515fb6"));

/** `Incoming call · +30***1391` — the activity line, with the masked party when there is one. */
export function eventLineOf(event: SipTrunkEventView): string {
  const party = event.partyMasked === null ? "" : ` · ${event.partyMasked}`;
  return `${event.verifiedByFirstCall === true ? VERIFIED_BY_FIRST_CALL_WORD() : EVENT_KIND_WORD[event.kind]}${party}`;
}

/** The field errors the domain validator (and the API, which runs it) names, in plain words. */
export const FIELD_ERROR_WORD: Readonly<Record<SipTrunkFieldErrorCode, string>> = {
  get required() { return portalText("operations", "fill_this_in_ec20f360"); },
  get wrong_type() { return portalText("operations", "this_value_is_not_valid_b90fce55"); },
  get empty() { return portalText("operations", "fill_this_in_ec20f360"); },
  get too_long() { return portalText("operations", "this_is_too_long_709d64b6"); },
  get out_of_range() { return portalText("operations", "this_is_out_of_range_5ea46e19"); },
  get not_an_option() { return portalText("operations", "choose_one_of_the_options_b33a1617"); },
  get not_printable() { return portalText("operations", "use_letters_digits_and_ordinary_punctuation_only_c39e6a22"); },
  get host_invalid() { return portalText("operations", "write_a_host_name_like_sip_provider_com_or_a_public_ip_add_efd5b90c"); },
  get host_ipv6_unsupported() { return portalText("operations", "ipv6_hosts_are_not_supported_by_wetalk_s_sip_edge_yet_writ_955b32cd"); },
  get srtp_requires_tls() { return portalText("operations", "always_on_encryption_needs_tls_as_the_transport_94968167"); },
  get not_e164() { return portalText("operations", "write_the_number_in_international_form_with_like_302101234_b05265b5"); },
  get not_in_national_country() { return portalText("operations", "this_number_is_not_in_the_country_chosen_for_the_national_c05991c2"); },
  get duplicate() { return portalText("operations", "this_is_listed_twice_17eddbb4"); },
  get too_many() { return portalText("operations", "that_is_more_than_a_trunk_can_hold_5753b95a"); },
  get address_invalid() { return portalText("operations", "write_an_ip_address_or_a_range_as_address_prefix_for_examp_af7e42af"); },
  get address_host_bits_set() { return portalText("operations", "write_the_range_from_its_first_address_for_example_0_24_28b615a1"); },
  get address_too_broad() { return portalText("operations", "that_range_is_too_wide_at_most_16_for_ipv4_32_for_ipv6_b0bd330e"); },
  get address_not_public() { return portalText("operations", "that_is_not_a_public_address_da09c226"); },
  get country_invalid() { return portalText("operations", "choose_a_country_261a3099"); },
  get country_unreviewed() { return portalText("operations", "wetalk_cannot_ring_that_country_yet_6422d32a"); },
  get national_country_required() { return portalText("operations", "choose_the_country_for_the_national_form_ca4b8dc6"); },
  get national_rule_missing() { return portalText("operations", "wetalk_cannot_write_numbers_of_that_country_in_national_fo_ee335ae0"); },
  get username_not_a_caller_id() { return portalText("operations", "to_show_the_account_name_as_caller_id_the_username_may_use_ec311ddc"); },
  registration_needs_udp: REGISTRATION_NEEDS_UDP(),
  get unknown_field() { return portalText("operations", "wetalk_did_not_recognise_this_field_f2ec2cd7"); },
};

/** `Greece` for `GR`, from the browser's own region names; the code itself when it has none. */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([portalLocale()], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}
